import { useEffect, useState } from "react";
import { COLLECTIONS } from "@/firestore/collections";
import { useFirestoreQuery } from "@/firestore/hooks";
import type {
  ActivityEntryDoc,
  CardDoc,
  KycVerificationDoc,
  PaymentDoc,
  UserDoc,
  WithdrawalDoc,
} from "@/firestore/types";
import { ROW_LIMIT, windowQuery } from "./metrics";

export function useDashboard(start: number, enabled: boolean) {
  const created = windowQuery("createdAt", start);
  const users = useFirestoreQuery<UserDoc>(COLLECTIONS.users, created, enabled);
  const kyc = useFirestoreQuery<KycVerificationDoc>(COLLECTIONS.kycVerifications, created, enabled);
  const cards = useFirestoreQuery<CardDoc>(COLLECTIONS.cards, created, enabled);
  const activity = useFirestoreQuery<ActivityEntryDoc>(
    COLLECTIONS.activityEntries,
    windowQuery("occurredAt", start),
    enabled,
  );
  const withdrawals = useFirestoreQuery<WithdrawalDoc>(COLLECTIONS.withdrawals, created, enabled);
  const payments = useFirestoreQuery<PaymentDoc>(COLLECTIONS.payments, created, enabled);
  // One-field query: includes old stuck payouts and uses the existing single-field index.
  const openWithdrawals = useFirestoreQuery<WithdrawalDoc>(
    COLLECTIONS.withdrawals,
    {
      filters: [["state", "in", ["initiated", "burning", "processing"]]],
      limit: ROW_LIMIT + 1,
    },
    enabled,
  );
  return { users, kyc, cards, activity, withdrawals, payments, openWithdrawals };
}

/** Local time only; Firestore changes arrive over listeners, never on a polling loop. */
export function useDashboardClock() {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

export function usePageVisible() {
  const [visible, setVisible] = useState(document.visibilityState !== "hidden");
  useEffect(() => {
    const change = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", change);
    return () => document.removeEventListener("visibilitychange", change);
  }, []);
  return visible;
}

export type DashboardSources = ReturnType<typeof useDashboard>;
